<template>
    <v-container class="pa-0">

        <skeleton-loader v-if="loading" type="student-routines" />

        <!-- NO HIRED COACH -->
        <div v-else-if="!hasCoach" class="routines-empty-state">
            <v-icon size="34" color="txtMuted">mdi-account-search-outline</v-icon>
            <div class="empty-text">{{ $t('coach.no_hired_coach') }}</div>
            <v-btn color="primary" class="black--text normal-btn" depressed large @click="toCoaches">
                {{ $t('coach.search_coach') }}
            </v-btn>
        </div>

        <template v-else>
            <div class="routines-grid">
                <!-- LEFT: calendar + sessions -->
                <div class="routines-left">
                    <div class="cal-card">
                        <month-calendar :value="selectedDay" :events="daysWithRoutines" flat
                            :show-current="today" :month.sync="pickerDate" @click:date="onPickDate" />
                    </div>

                    <div class="sessions-card">
                        <div class="sessions-head">{{ $t('routines.month_sessions') }}</div>

                        <div v-if="selectedDay" class="sessions-filter">
                            <span>{{ $t('routines.showing_selected_day') }}</span>
                            <button type="button" class="sessions-filter-clear" @click="selectedDay = null">
                                {{ $t('routines.show_all') }}
                            </button>
                        </div>

                        <div v-if="sessionsFiltered.length === 0" class="sessions-empty">
                            {{ selectedDay ? $t('coach.no_assigned_routines_day') : $t('routines.no_sessions_month') }}
                        </div>

                        <button v-for="session in sessionsFiltered" :key="session.id" type="button"
                            class="session-item"
                            :class="{ selected: isPreviewed(session), future: isFuture(session), locked: isSessionLocked(session) }"
                            @click="onClickSession(session)">
                            <div class="session-left">
                                <span class="session-dot" :class="sessionDotClass(session)" />
                                <div class="session-info">
                                    <div class="session-date">{{ sessionPrimaryLabel(session) }}</div>
                                    <div class="session-meta">{{ sessionMeta(session) }}</div>
                                </div>
                            </div>
                            <div class="session-right">
                                <span class="session-chip" :class="sessionChipClass(session)">{{ sessionChip(session) }}</span>
                                <v-icon size="18" color="txtMuted">
                                    {{ isSessionLocked(session) ? 'mdi-lock-outline' : 'mdi-chevron-right' }}
                                </v-icon>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- RIGHT: stats + preview (desktop) / next session (mobile) -->
                <div class="routines-right">
                    <div class="stats-row">
                        <home-stat-card :value="completedCount" :label="$t('common.completeds')"
                            value-class="green-color" :delta="$t('routines.this_month')" delta-state="neutral" />
                        <home-stat-card :value="pendingCount" :label="$t('routines.pending')" value-class="gradient"
                            :delta="$t('routines.this_month')" delta-state="neutral" />
                        <home-stat-card :value="`${adherence}%`" :label="$t('routines.adherence')"
                            value-class="teal-color" :delta="$t('routines.this_month')" delta-state="neutral" />
                    </div>

                    <template v-if="!isMobile">
                        <competence-preview v-if="previewSession && previewSession.type === 'competence'"
                            :competence="previewSession" @start="startSession" />
                        <routine-preview v-else-if="previewSession" :routine="previewSession" @start="startSession"
                            :name-label="isPlaylistNumerationName ? sessionDayLabel(previewSession) : null"
                            :date-text="isPlaylistNumerationName ? '' : null" />
                        <div v-else class="next-card">
                            <div class="next-title">{{ $t('routines.next_session') }}</div>
                            <div class="next-empty">
                                <v-icon size="30" color="txtMuted">mdi-check-circle-outline</v-icon>
                                <span>{{ $t('common.no_next_routines') }}</span>
                            </div>
                        </div>
                    </template>

                    <div v-else class="next-card">
                        <div class="next-title">{{ $t('routines.next_session') }}</div>
                        <div v-if="nextSession" class="next-body">
                            <div class="next-date">{{ sessionPrimaryLabel(nextSession) }}</div>
                            <div class="next-meta">{{ sessionMeta(nextSession) }}</div>
                            <v-btn block large depressed color="primary" class="black--text next-btn"
                                @click="startSession(nextSession)">
                                {{ $t('routines.start_session') }}
                            </v-btn>
                        </div>
                        <div v-else class="next-empty">
                            <v-icon size="30" color="txtMuted">mdi-check-circle-outline</v-icon>
                            <span>{{ $t('common.no_next_routines') }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ROUTINE DETAIL (mobile) -->
            <routine-detail-dialog ref="routineDetail" />

            <!-- FAB + student event dialogs -->
            <fab-menu :items="options" with-labels left-tooltip fixed-bottom @click:injury="handleInjury"
                @click:activity="handleActivity" @click:travel="handleTravel" @click:illness="handleIllness"
                @click:competence="handleCompetence" />

            <injury-form-dialog :studentId="studentId" :coachId="coachId" ref="injury" />
            <add-activity-dialog ref="activity" />
            <travel-form-dialog :studentId="studentId" :coachId="coachId" ref="travel" />
            <illness-form-dialog :studentId="studentId" :coachId="coachId" ref="illness" />
            <add-competence-dialog :current-selection="currentSelection" collection="assignedRoutines"
                :coach-id="coachId" ref="competence" @saved="onSaveCompetence" select-date />
        </template>

    </v-container>
</template>

<script>

import { mapActions, mapMutations, mapState } from 'vuex'
import { isRoutineUnlocked, routineUnlockDate } from '@/utils/Playlist'
import SkeletonLoader from "@/components/common/SkeletonLoader";
import MonthCalendar from "@/components/common/MonthCalendar";
import HomeStatCard from "@/components/student/home/HomeStatCard";
import RoutinePreview from "@/components/student/RoutinePreview";
import CompetencePreview from "@/components/student/CompetencePreview";
import RoutineDetailDialog from '@/components/student/RoutineDetailDialog'
import FabMenu from '@/components/common/FabMenu.vue';
import InjuryFormDialog from '@/components/student/InjuryFormDialog.vue';
import AddActivityDialog from '@/components/student/AddActivityDialog.vue';
import TravelFormDialog from '@/components/student/TravelFormDialog.vue';
import IllnessFormDialog from '@/components/student/IllnessFormDialog.vue';
import AddCompetenceDialog from '@/components/coach/competences/AddCompetenceDialog.vue';
import { sendMessage, MESSAGE_COLORS } from "@/utils/Messages";

export default {
    components: {
        SkeletonLoader,
        MonthCalendar,
        HomeStatCard,
        RoutinePreview,
        CompetencePreview,
        RoutineDetailDialog,
        FabMenu,
        InjuryFormDialog,
        AddActivityDialog,
        TravelFormDialog,
        IllnessFormDialog,
        AddCompetenceDialog,
    },
    data() {
        return {
            loading: true,
            pickerDate: null,
            selectedDay: null,
            selectedSession: null,
            options: [
                { icon: 'mdi-soccer-field', value: 'competence', tooltip: this.$t('routines.fab_competence') },
                { icon: 'mdi-calendar-plus', value: 'activity', tooltip: this.$t('routines.fab_activity') },
                { icon: 'mdi-airplane', value: 'travel', tooltip: this.$t('routines.fab_travel') },
                { icon: 'mdi-emoticon-sick-outline', value: 'illness', tooltip: this.$t('routines.fab_illness') },
                { icon: 'mdi-account-injury', value: 'injury', tooltip: this.$t('routines.fab_injury') },
            ]
        }
    },
    computed: {
        ...mapState(['routinesStudent']),
        today(vm = this) {
            return vm.$moment().format('YYYY-MM-DD')
        },
        hasCoach(vm = this) {
            return !!vm.account?.studentSubscription
        },
        monthLabel(vm = this) {
            const ref = vm.pickerDate || vm.$moment().format('YYYY-MM')
            const label = vm.$moment(ref).format('MMMM YYYY')
            return label.charAt(0).toUpperCase() + label.slice(1)
        },
        sessions(vm = this) {
            return [...(vm.routinesStudent || [])].sort((a, b) => {
                return (a.rawDate?.full || '').localeCompare(b.rawDate?.full || '')
            })
        },
        sessionsFiltered(vm = this) {
            if (!vm.selectedDay) return vm.sessions
            return vm.sessions.filter(s => s.rawDate?.full === vm.selectedDay)
        },
        monthCount(vm = this) {
            return vm.sessions.length
        },
        completedCount(vm = this) {
            return vm.sessions.filter(s => s.advance?.post).length
        },
        pendingCount(vm = this) {
            return vm.sessions.filter(s => !s.advance?.post).length
        },
        adherence(vm = this) {
            if (vm.sessions.length === 0) return 0
            return Math.round((vm.completedCount / vm.sessions.length) * 100)
        },
        nextSession(vm = this) {
            const firstPending = vm.sessions.find(s => !s.advance?.post)
            if (vm.isPlaylistNumerationName) return firstPending || null
            const upcoming = vm.sessions.find(s => !s.advance?.post && (s.rawDate?.full || '') >= vm.today)
            return upcoming || firstPending || null
        },
        previewSession(vm = this) {
            if (vm.selectedSession) return vm.selectedSession
            if (vm.selectedDay) return vm.sessionsFiltered.find(s => s.type !== 'competence') || vm.sessionsFiltered[0] || null
            return vm.nextSession
        },
        subtitleText(vm = this) {
            if (!vm.hasCoach) return ''
            return `${vm.monthLabel} · ${vm.$tc('routines.sessions_scheduled', vm.monthCount, { n: vm.monthCount })}`
        },
        playlist(vm = this) {
            return vm.account?.playlist || null
        },
        isPlaylistOnceLimit(vm = this) {
            return vm.account?.playlist?.limit === 'once'
        },
        isPlaylistNumerationName(vm = this) {
            return vm.account?.playlist?.name === 'numeration'
        },
        dateIndexMap(vm = this) {
            const map = {}
            let index = 0
            for (const session of vm.sessions) {
                const date = session.rawDate?.full
                if (!date || (date in map)) continue
                index++
                map[date] = index
            }
            return map
        },
        routineDatesSet(vm = this) {
            return new Set(vm.sessions.map(s => s.rawDate?.full).filter(Boolean))
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        },
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        currentSelection(vm = this) {
            return { ...vm.account, objectID: vm.studentId }
        },
        queryDate(vm = this) {
            return vm.$route.query.date || null
        },
    },
    mounted() {
        const vm = this
        if (!vm.hasCoach) {
            vm.loading = false
            return
        }
        const queryMoment = vm.queryDate ? vm.$moment(vm.queryDate) : null
        if (queryMoment && queryMoment.isValid()) {
            vm.selectedDay = queryMoment.format('YYYY-MM-DD')
            vm.pickerDate = queryMoment.format('YYYY-MM')
        } else {
            vm.pickerDate = vm.$moment().format('YYYY-MM')
        }
    },
    methods: {
        ...mapMutations(['setShowCalendar', 'setTitle', 'setToolbarSubtitle']),
        ...mapActions(['fetchRoutinesStudent']),
        async loadRoutines(date) {
            const vm = this
            await vm.withLoading(async () => {
                await vm.fetchRoutinesStudent({ studentId: vm.studentId, date })
            })
        },
        daysWithRoutines(date) {
            return this.routineDatesSet.has(date)
        },
        isFuture(session) {
            return (session.rawDate?.full || '') > this.today
        },
        sessionDate(session) {
            const date = session.rawDate?.full
            if (!date) return ''
            const label = this.$moment(date).format(this.$t('common.date_format_day_month'))
            return label.charAt(0).toUpperCase() + label.slice(1)
        },
        sessionDayLabel(session) {
            const date = session.rawDate?.full
            return `${this.$t('common.day')} ${this.dateIndexMap[date] || ''}`.trim()
        },
        sessionPrimaryLabel(session) {
            return this.isPlaylistNumerationName ? this.sessionDayLabel(session) : this.sessionDate(session)
        },
        isSessionLocked(session) {
            const vm = this
            if (vm.isPlaylistOnceLimit && session.advance?.post) return true
            return !isRoutineUnlocked(vm.playlist, session.rawDate?.full, vm.today)
        },
        lockedUntilLabel(session) {
            const vm = this
            const date = routineUnlockDate(vm.playlist, session.rawDate?.full)
            if (!date) return null
            return vm.$t('common.routine_locked_until', { date: vm.$moment(date).format('DD-MM-YYYY') })
        },
        notifyLocked(session) {
            const vm = this
            const label = vm.lockedUntilLabel(session)
            if (label) vm.toast(label)
        },
        sessionDuration(session) {
            let minutes = 0;
            (session.blocks || []).forEach(block => {
                (block.exercises || []).forEach(exercise => {
                    minutes += exercise?.config?.sets?.length || block.sets || 0
                })
            })
            const minutesAdded = Number(minutes * 1.3).toFixed()
            if (Number(minutes) === Number(minutesAdded)) return `${minutes}`
            return `${minutes} - ${minutesAdded}`
        },
        sessionMeta(session) {
            if (session.type === 'competence') return this.$t('common.competence')
            const blocks = session.blocks?.length || 0
            return `${blocks} ${this.$tc('common.blocks', blocks).toLowerCase()} · ${this.sessionDuration(session)} ${this.$t('common.short_minutes')}`
        },
        sessionDotClass(session) {
            if (session.advance?.post) return 'done'
            if (session.advance?.pre) return 'in-progress'
            return ''
        },
        sessionChip(session) {
            if (session.advance?.post) return this.$t('exercises.complete')
            if (session.advance?.pre) return this.$t('routines.in_progress')
            return this.$t('routines.pending_one')
        },
        sessionChipClass(session) {
            if (session.advance?.post) return 'done'
            if (session.advance?.pre) return 'in-progress'
            return 'pending'
        },
        onPickDate(date) {
            const vm = this
            vm.selectedDay = vm.selectedDay === date ? null : date
        },
        isPreviewed(session) {
            const vm = this
            return !vm.isMobile && vm.previewSession && vm.previewSession.id === session.id
        },
        onClickSession(session) {
            const vm = this
            if (vm.isSessionLocked(session)) return vm.notifyLocked(session)
            if (session.type === 'competence') {
                if (vm.isMobile) vm.pushRoute({ name: 'student.routine.competence.detail', params: { uid: session.id } })
                else vm.selectedSession = session
                return
            }
            if (vm.isMobile) {
                vm.$refs.routineDetail.setRoutine(session, vm.isPlaylistNumerationName ? vm.sessionDayLabel(session) : null)
                vm.$refs.routineDetail.show()
            } else {
                vm.selectedSession = session
            }
        },
        startSession(session) {
            const vm = this
            if (!session) return
            if (vm.isSessionLocked(session)) return vm.notifyLocked(session)
            if (session.type === 'competence') {
                vm.pushRoute({ name: 'student.routine.competence.detail', params: { uid: session.id } })
                return
            }
            vm.pushRoute({ name: 'student.routine.detail', params: { uid: session.id } })
        },
        toCoaches() {
            this.$router.push({ name: 'student.coaches' })
        },
        handleInjury() {
            this.$refs.injury.show()
        },
        handleActivity() {
            this.$refs.activity.show()
        },
        handleTravel() {
            this.$refs.travel.show()
        },
        handleIllness() {
            this.$refs.illness.show()
        },
        handleCompetence() {
            this.$refs.competence.show()
        },
        async onSaveCompetence(payload) {
            const vm = this
            const date = vm.$moment(payload.date).format('YYYY-MM-DD')
            await vm.loadRoutines(date)
            vm.sendMessageToCoach(payload)
        },
        async sendMessageToCoach(competence) {
            const vm = this
            const message = `${vm.$t('common.recorded_competence')} \n${vm.$t('common.date')}: ${vm.$moment(competence.date).format('DD-MM-YYYY')} \n${vm.$t('common.versus_short')}: ${competence.versus}`
            await sendMessage({ message, from: vm.studentId, color: MESSAGE_COLORS.competence }, vm.coachId, vm.studentId)
        },
    },
    watch: {
        routinesStudent() {
            const vm = this
            if (vm.selectedSession) {
                vm.selectedSession = vm.sessions.find(s => s.id === vm.selectedSession.id) || null
            }
        },
        subtitleText: {
            immediate: true,
            handler(val) {
                this.setToolbarSubtitle(val)
            },
        },
        async pickerDate(val) {
            const vm = this
            if (!val) return
            if (!vm.$moment(val, 'YYYY-MM', true).isValid()) return
            const date = vm.$moment(val).format('YYYY-MM-DD')
            await vm.loadRoutines(date)
        },
    },
    beforeRouteLeave(to, from, next) {
        const vm = this
        vm.setShowCalendar(true)
        vm.setToolbarSubtitle('')
        vm.setTitle('')
        next()
    }
}
</script>

<style scoped>
.routines-grid {
    display: grid;
    grid-template-columns: minmax(320px, 400px) minmax(0, 1fr);
    gap: 20px;
    align-items: start;
}

.routines-left,
.routines-right {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

/* Calendar */
.cal-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 6px;
    overflow: hidden;
}

/* Sessions list */
.sessions-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 6px 16px;
}

.sessions-head {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-txtMuted-base);
    padding: 12px 0 8px;
}

.sessions-filter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    padding: 6px 0 10px;
}

.sessions-filter-clear {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-accent-base);
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
}

.theme--dark .sessions-filter-clear {
    color: var(--v-primary-base);
}

.sessions-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}

.session-item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 0;
    border: none;
    border-top: 1px solid var(--v-outline-base);
    background: transparent;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    transition: opacity 0.15s;
}

.session-item:first-of-type {
    border-top: none;
}

.session-item:hover {
    opacity: 1;
}

.session-item.future {
    opacity: 0.55;
}

.session-item.locked {
    opacity: 0.5;
    cursor: default;
}

.session-item.selected {
    opacity: 1;
    background: var(--v-surface2-base);
    border-top-color: transparent;
    border-radius: 10px;
    margin: 2px -8px;
    padding: 12px 8px;
    width: calc(100% + 16px);
}

.session-item.selected + .session-item {
    border-top-color: transparent;
}

.session-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.session-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid var(--v-txtMuted-base);
    flex-shrink: 0;
}

.session-dot.done {
    background: var(--v-primary-base);
    border-color: var(--v-primary-base);
}

.session-dot.in-progress {
    background: #F5A623;
    border-color: #F5A623;
}

.session-info {
    min-width: 0;
}

.session-date {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.session-meta {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.session-right {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

.session-chip {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 11px;
    white-space: nowrap;
}

.session-chip.done {
    background: var(--v-primary-base);
    color: #0C0E12;
}

.session-chip.pending {
    background: var(--v-surface2-base);
    color: var(--v-txtMuted-base);
}

.session-chip.in-progress {
    background: rgba(245, 166, 35, 0.16);
    color: #B45309;
}

.theme--dark .session-chip.in-progress {
    color: #F5B841;
}

/* Right column */
.stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.next-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 16px 18px;
}

.next-title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-txtMuted-base);
    margin-bottom: 14px;
}

.next-body {
    text-align: center;
}

.next-date {
    font-size: 26px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.next-meta {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    margin: 4px 0 18px;
}

.next-btn {
    border-radius: 12px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0;
}

.next-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 24px 0;
    color: var(--v-txtMuted-base);
    font-size: 13px;
    text-align: center;
}

/* Empty state (no coach) */
.routines-empty-state {
    max-width: 420px;
    margin: 24px auto 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.empty-text {
    font-size: 15px;
    color: var(--v-txtSecondary-base);
}

@media (max-width: 959px) {
    .routines-grid {
        grid-template-columns: 1fr;
    }
}
</style>
